import { assertSafeInteger } from '@kenocyber/shared';
import { OFFLINE_FARM_CAP_SECONDS, OFFLINE_FARM_EFFICIENCY_BPS } from '../offline';

/**
 * Offline farm, computed from VALIDATED checkpoints.
 *
 * The decided rules live in `../offline.ts` (6 hour cap, 70% efficiency, never
 * items) and are imported rather than restated - one place to change them.
 * This module adds the part that needs history: what rate to extrapolate.
 *
 * Everything here is server-side arithmetic over server timestamps. No client
 * clock, no client-reported earnings: the samples are rows the server itself
 * wrote when it re-simulated a checkpoint.
 */

/** How much recent validated play backs the average. */
export const OFFLINE_RATE_WINDOW_MS = 10 * 60 * 1000;
export const OFFLINE_FARM_CAP_MS = OFFLINE_FARM_CAP_SECONDS * 1000;

/** One validated checkpoint, as persisted. */
export interface CheckpointSample {
  /** Server time the checkpoint was accepted, epoch ms. */
  readonly recordedAtMs: number;
  /** Simulated time the server actually credited, ms. */
  readonly validatedMs: number;
  readonly xpEarned: bigint;
  readonly goldEarned: bigint;
  /** Loadout the sample was earned under; only equal fingerprints average. */
  readonly fingerprint: string;
}

export interface OfflineFarmRequest {
  /** Server time of the last validated checkpoint, epoch ms. */
  readonly lastCheckpointAtMs: number;
  /** Server time now, epoch ms. NEVER a browser clock. */
  readonly nowMs: number;
  /** Fingerprint the player returns with. */
  readonly fingerprint: string;
  readonly samples: readonly CheckpointSample[];
}

export interface OfflineFarmGrant {
  /** Offline time credited after the 6 h cap, ms. */
  readonly creditedMs: number;
  /** Validated time that backed the rate. Zero means no payout was possible. */
  readonly sampleMs: number;
  readonly xpGained: bigint;
  readonly goldGained: bigint;
  /** Offline never produces items. Explicit so no caller can "forget". */
  readonly itemsGained: 0;
}

const EMPTY_GRANT: OfflineFarmGrant = Object.freeze({
  creditedMs: 0,
  sampleMs: 0,
  xpGained: 0n,
  goldGained: 0n,
  itemsGained: 0,
});

/**
 * How much XP and Gold a player earned while away.
 *
 *   rate      = sum(sample earnings) / sum(sample validated ms)
 *               over checkpoints of the SAME fingerprint inside the last
 *               10 minutes of validated play
 *   credited  = min(now - lastCheckpoint, 6 h)
 *   payout    = floor(rate x credited x 70%)
 *
 * The division is integer `bigint` arithmetic end to end: no float ever
 * touches a value that becomes a ledger row. Anything unfunded - no samples,
 * a different loadout, a clock that went backwards - pays exactly zero.
 */
export function computeOfflineFarmFromCheckpoints(request: OfflineFarmRequest): OfflineFarmGrant {
  assertSafeInteger(request.lastCheckpointAtMs, 'lastCheckpointAtMs');
  assertSafeInteger(request.nowMs, 'nowMs');

  const elapsedMs = request.nowMs - request.lastCheckpointAtMs;
  if (elapsedMs <= 0) {
    // Includes a tampered or rewound clock: never a reward, never an error.
    return EMPTY_GRANT;
  }
  const creditedMs = Math.min(elapsedMs, OFFLINE_FARM_CAP_MS);

  // Only play that is comparable to the current setup, and recent: the window
  // ends at the last checkpoint, so a long absence cannot widen it.
  const windowStart = request.lastCheckpointAtMs - OFFLINE_RATE_WINDOW_MS;
  let sampleMs = 0;
  let xpTotal = 0n;
  let goldTotal = 0n;
  for (const sample of request.samples) {
    if (sample.fingerprint !== request.fingerprint) {
      continue;
    }
    if (sample.recordedAtMs <= windowStart || sample.recordedAtMs > request.lastCheckpointAtMs) {
      continue;
    }
    if (!Number.isSafeInteger(sample.validatedMs) || sample.validatedMs <= 0) {
      continue;
    }
    if (sample.xpEarned < 0n || sample.goldEarned < 0n) {
      continue;
    }
    sampleMs += sample.validatedMs;
    xpTotal += sample.xpEarned;
    goldTotal += sample.goldEarned;
  }

  // At least one complete checkpoint is required; without it there is no rate
  // to extrapolate and the honest answer is zero.
  if (sampleMs <= 0) {
    return { ...EMPTY_GRANT, creditedMs };
  }

  const numerator = BigInt(creditedMs) * BigInt(OFFLINE_FARM_EFFICIENCY_BPS);
  const denominator = BigInt(sampleMs) * 10_000n;
  return {
    creditedMs,
    sampleMs,
    // Weighted by time, floored to whole units - never rounded up.
    xpGained: (xpTotal * numerator) / denominator,
    goldGained: (goldTotal * numerator) / denominator,
    itemsGained: 0,
  };
}
